import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, EMPTY } from 'rxjs';
import { AuthService } from '../services/auth.service';

function isAuthExemptUrl(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/authenticate') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  );
}

function extractApiMessage(error: HttpErrorResponse): string | undefined {
  const body = error.error;
  if (body && typeof body === 'object' && typeof body.message === 'string') {
    return body.message;
  }
  return undefined;
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);

  // No interceptar llamadas de auth (login, refresh, logout)
  if (isAuthExemptUrl(req.url)) {
    return next(req);
  }

  const token = authService.getAccessToken();

  if (token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      const apiMessage = extractApiMessage(error);

      // Sin access token, o sesión ya cerrada en otro repo/pestaña.
      if (!token || !authService.getAccessToken()) {
        authService.handleSessionExpired(apiMessage);
        return EMPTY;
      }

      // Con token: intentar refresh; si falla, mismo flujo de sesión expirada
      return authService.refreshToken().pipe(
        switchMap((response) => {
          // ApiResponse.data es opcional (puede omitirse con @JsonInclude(NON_NULL)).
          // Si el back no devolvió tokens nuevos, no hay forma de reintentar.
          const newToken = response.data?.accessToken;
          if (!newToken) {
            authService.handleSessionExpired(apiMessage);
            return EMPTY;
          }
          const retryReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          return next(retryReq);
        }),
        catchError(() => {
          authService.handleSessionExpired(apiMessage);
          return EMPTY;
        }),
      );
    }),
  );
};
